import { Chip, Box, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useLayoutEffect, useRef, useState } from 'react';
import Iconify from '../iconify';
import { fDateTime, fMonthDayYear } from '../../utils/formatTime';

const facility = {
  field: 'facility',
  headerName: 'Facility',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  flex: 1,
  minWidth: 90,
  type: 'customSelect',
};

const group = {
  field: 'company',
  headerName: 'Group',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  flex: 1,
  minWidth: 90,
  type: 'customSelect',
};

const lastLogin = {
  field: 'lastLoggedIn',
  headerName: 'Last Login',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {fDateTime(params.value, "MM/dd/yyyy p")}
    </Box>
  ),
  flex: 1,
  minWidth: 80,
  type: 'customDate',
};

const fullName = {
  field: 'fullName',
  headerName: 'Name',
  flex: 1.2,
  minWidth: 80,
  renderCell: (params) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        component="div"
        sx={{
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          overflow: 'hidden',
        }}
      >
        {params.row.firstName} {params.row.lastName}
      </Box>
      <Box
        component="div"
        sx={{
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          overflow: 'hidden',
          fontSize: '0.7rem',
        }}
      >
        {params.row.email}
      </Box>
    </Box>
  ),
  valueGetter: (params) => {
    const { firstName, lastName, email } = params.row;
    return `${firstName} ${lastName} ${email}`;
  },
  getFilterValues: (row) => {
    const { firstName, lastName, email } = row;
    return [`${firstName} ${lastName} ${email}`];
  },
  type: 'customSelect',
};

const position = { field: 'position', headerName: 'Position', flex: 1, minWidth: 90, type: 'customSelect' };

const getStatus = (params) => {
  if (params.row.deleteFlag) return "Deleted";
  if (new Date(params.row.lockoutEnd) > new Date()) return "Disabled";
  if (params.row.emailConfirmed) return "Active";
  return "Pending";
}

const STATUS_COLORS = {
  Deleted: { color: 'error.main', bgcolor: '#FDEDED' },
  Disabled: { color: 'error.main', bgcolor: '#FDEDED' },
  Active: { color: '#008236', bgcolor: '#DCFCE7' },
  Pending: { color: '#CB9853', bgcolor: '#FDF5E9' },
};

const emailConfirmed = {
  field: 'emailConfirmed',
  headerName: 'Status',
  flex: 0.5,
  getFilterValues: (row) => [getStatus({row})],
  valueGetter: getStatus,
  renderCell: (params) => (
    <Tooltip title={params.row.deleteFlag ? `Deleted by ${params.row.deletedBy} on: ${fMonthDayYear(params.row.dateDeleted)}` :
      new Date(params.row.lockoutEnd) > new Date() ? `Disabled by ${params.row.disabledBy} on: ${fMonthDayYear(params.row.dateDisabled)}` :
        params.row.emailConfirmed ? 'Email Confirmed' : 'Awaiting Email Confirmation'} >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 0.75,
          py: 0.25,
          borderRadius: '5px',
          fontFamily: '"Public Sans"',
          fontSize: 12,
          fontWeight: 500,
          lineHeight: '18px',
          whiteSpace: 'normal',
          ...STATUS_COLORS[getStatus(params)],
        }}
      >
        {getStatus(params)}
      </Box>
    </Tooltip>
  ),
  type: 'customSelect',
};

const getRoles = (row) =>
  row?.roleShort
  ? row.roleShort.split(',').map((role) => role.trim()).filter((role) => role)
  : [];

const ROLE_CHIP_GAP = 4;
const ROLE_CHIP_MAX_LINES = 2;

const countChipLines = (widths, maxWidth) => {
  let lines = 1;
  let lineWidth = 0;
  widths.forEach((width) => {
    const nextWidth = lineWidth ? lineWidth + ROLE_CHIP_GAP + width : width;
    if (lineWidth && nextWidth > maxWidth) {
      lines += 1;
      lineWidth = width;
    } else {
      lineWidth = nextWidth;
    }
  });
  return lines;
};

const getVisibleChipCount = (chipWidths, moreWidth, maxWidth) => {
  if (countChipLines(chipWidths, maxWidth) <= ROLE_CHIP_MAX_LINES) return chipWidths.length;
  for (let count = chipWidths.length - 1; count > 0; count -= 1) {
    if (countChipLines([...chipWidths.slice(0, count), moreWidth], maxWidth) <= ROLE_CHIP_MAX_LINES) return count;
  }
  return 0;
};

// Shows as many role chips as fit in 2 lines, the rest go into a "+ X more" chip with a tooltip
function RoleChips({ roles }) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(roles.length);
  const rolesKey = roles.join(',');

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return undefined;

    const calculate = () => {
      const chipWidths = [...measure.children].map((child) => child.offsetWidth);
      const moreWidth = chipWidths.pop();
      setVisibleCount(getVisibleChipCount(chipWidths, moreWidth, container.clientWidth));
    };

    calculate();
    const observer = new ResizeObserver(calculate);
    observer.observe(container);
    return () => observer.disconnect();
  }, [rolesKey]);

  const hiddenRoles = roles.slice(visibleCount);

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%', py: 0.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${ROLE_CHIP_GAP}px` }}>
        {roles.slice(0, visibleCount).map((role, index) => (
          <Chip key={index} label={role} size="small" sx={{ maxWidth: '100%' }} />
        ))}
        {hiddenRoles.length > 0 && (
          <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{hiddenRoles.join('\n')}</Box>}>
            <Chip label={`+ ${hiddenRoles.length} more`} size="small" />
          </Tooltip>
        )}
      </Box>
      <Box
        ref={measureRef}
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: 0,
          overflow: 'hidden',
          visibility: 'hidden',
          display: 'flex',
          whiteSpace: 'nowrap',
          '& > *': { flexShrink: 0 },
        }}
      >
        {roles.map((role, index) => (
          <Chip key={index} label={role} size="small" />
        ))}
        <Chip label={`+ ${roles.length} more`} size="small" />
      </Box>
    </Box>
  );
}

RoleChips.propTypes = {
  roles: PropTypes.array,
};

const roleShort = {
  field: 'roleShort',
  headerName: 'Permissions',
  flex: 3,
  renderCell: (params) => {
    const roles = getRoles(params.row);
    if (!roles.length) return null;
    return <RoleChips roles={roles} />;
  },
  valueGetter: (params) => getRoles(params.row)?.join(', '),
  getFilterValues: (row) =>  getRoles(row) || [],
  customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
  type: 'customSelect',
};

const name = {
  field: 'name',
  flex: 1,
  minWidth: 80,
  headerName: 'Name',
  renderCell: (params) => (
    <Box sx={{ wordWrap: 'break-word', overflow: 'hidden', whiteSpace: 'pre-wrap', textOverflow: 'ellipsis' }}>
      {params.value}
    </Box>
  ),
};

const useUserFacility = {
  field: 'useUserFacility',
  headerName: 'Use user facility',
  flex: 1,
  renderCell: (params) => {
    const { value } = params;
    return value ? (
      <Iconify icon="material-symbols:check-circle-outline" color="primary" />
    ) : (
      <Iconify icon="mdi:close" color="error" />
    );
  },
};

const label = { field: 'label', headerName: 'Role', flex: 1, minWidth: 70, type: 'customSelect' };
const description = {
  field: 'description',
  headerName: 'Description',
  minWidth: 100,
  flex: 1.5,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        fontSize: '0.7rem',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  type: 'customSelect',
};

const dateAdded = {
  field: 'dateAdded',
  headerName: 'Date Added',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {fDateTime(params.value, "MM/dd/yyyy p")}
    </Box>
  ),
  flex: 1,
  minWidth: 80,
  type: 'customDate',
};

const addedBy = {
  field: 'addedBy',
  headerName: 'Added By',
  flex: 1,
  minWidth: 70 ,
  type: 'customSelect',
};

const USERS_COLUMNS = [fullName, group, facility, position, lastLogin, dateAdded, addedBy, emailConfirmed, roleShort ];
const USERS_COLUMNS_REPORT = [fullName, facility, position, lastLogin ];
const COPYUSERS_COLUMNS = [fullName, facility, position, roleShort ];
const ROLES_COLUMNS = [label, description];
const TEMPLATES_COLUMNS = [name, useUserFacility];

export { USERS_COLUMNS, COPYUSERS_COLUMNS, ROLES_COLUMNS, TEMPLATES_COLUMNS, USERS_COLUMNS_REPORT };
